import { useContext, useEffect, useState } from "react";
import { useMatcher, MatcherView } from "utils/MatcherProvider";
import { useUnmatchRecordsMutation, useQueryMatchedViewsPhaseDataQuery } from "graphql/types/graphql";
import enqueueError from "utils/enqueueError";
import { enqueueSnackbar } from "notistack";
import { context } from "../../../../utils/Provider";

interface PhaseTableRow {
  FX_UNIQUE_ID?: string;
  [key: string]: unknown;
}

export const useUnmatch = () => {
  const { setIsLoading, selectedCube } = useContext(context);
  const { phaseData, setPhaseData, changeView } = useMatcher();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [tableData, setTableData] = useState<PhaseTableRow[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [remainingRows, setRemainingRows] = useState(0);

  const { data, loading, error, refetch } = useQueryMatchedViewsPhaseDataQuery({
    notifyOnNetworkStatusChange: remainingRows <= 1,
    variables: {
      cubeName: selectedCube.name,
      viewName: phaseData.tableName,
      identifier: phaseData.identifier.toString(),
      phase: phaseData.phase,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    },
    skip: !phaseData.tableName || phaseData.phase === -1,
    onCompleted: (result) => {
      setIsLoading(false);
      if (result?.queryMatchedViewsPhaseData) {
        const edges = result.queryMatchedViewsPhaseData.edges;
        setTableData(edges);
        setTotalCount(result.queryMatchedViewsPhaseData.totalCount);
        setPhaseData({
          ...phaseData,
          matchedCount: result.queryMatchedViewsPhaseData.totalCount,
        });
      }
    },
    onError: (error) => {
      setIsLoading(false);
      enqueueError(error);
    },
    fetchPolicy: "cache-and-network",
  });

  const [unmatch, { loading: unmatchLoading }] = useUnmatchRecordsMutation({
    update: (cache, { data }) => {
      if (data?.unmatchRecords?.success) {
        cache.evict({ id: "ROOT_QUERY", fieldName: "queryMatchedViewsPhaseData" });

        setTableData((prev) => {
          const newTableData = prev.filter((row) => !selectedRows.includes(row.FX_UNIQUE_ID || ""));
          setRemainingRows(newTableData.length);
          return newTableData;
        });

        setTotalCount((prev) => prev - selectedRows.length);
        const newMatchedCount = Math.max(0, phaseData.matchedCount - selectedRows.length);
        setPhaseData({
          ...phaseData,
          matchedCount: newMatchedCount,
        });
        setSelectedRows([]);
        enqueueSnackbar(`Successfully unmatched ${selectedRows.length} records`, { variant: "success" });
      }
    },
    onError: (error) => {
      enqueueError(error);
    },
  });

  useEffect(() => {
    setIsLoading(loading || unmatchLoading);
  }, [loading, unmatchLoading, setIsLoading]);

  useEffect(() => {
    if (remainingRows <= 1) {
      refetch();
    }
  }, [refetch, remainingRows]);

  const allColumns =
    tableData.length > 0
      ? Object.keys(tableData[0]).map((key) => ({
          header: key.toUpperCase(),
          accessor: key,
        }))
      : [];

  const handleSelectRow = (fxUniqueId: string) => {
    setSelectedRows((prev) =>
      prev.includes(fxUniqueId) ? prev.filter((id) => id !== fxUniqueId) : [...prev, fxUniqueId]
    );
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows(tableData.map((row) => row.FX_UNIQUE_ID || ""));
    } else {
      setSelectedRows([]);
    }
  };

  const handleUnmatch = async () => {
    if (selectedRows.length === 0) {
      enqueueSnackbar("No rows selected for unmatching", { variant: "warning" });
      return;
    }

    try {
      setIsLoading(true);
      await unmatch({
        variables: {
          cubeId: phaseData.cubeId,
          tableName: phaseData.tableName,
          identifier: phaseData.identifier.toString(),
          ids: selectedRows,
        },
      });

      setRemainingRows(tableData.filter((row) => !selectedRows.includes(row.FX_UNIQUE_ID || "")).length);
    } catch (e) {
      enqueueSnackbar(`Error unmatching records: ${e instanceof Error ? e.message : "Something went wrong"}`, {
        variant: "error",
        autoHideDuration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResultsView = () => {
    changeView(MatcherView.RESULTS);
  };

  return {
    phaseData,
    selectedRows,
    tableData,
    page,
    rowsPerPage,
    totalCount,
    loading,
    error,
    unmatchLoading,
    allColumns,
    handleSelectRow,
    handleSelectAll,
    handleUnmatch,
    handleChangePage,
    handleChangeRowsPerPage,
    handleResultsView,
  };
};
